/**
 * Antrenman motoru — saf mantık. Gerekçeler ve kaynaklar: MOTOR.md §4
 *
 * Üç iş yapıyor:
 *  1. Yapılan işi kas bazında sayıyor (fraksiyonel hacim)
 *  2. Gün sayısı değişince hacmi yeniden dağıtıyor
 *  3. Bir sonraki antrenmanın yükünü öneriyor (çift ilerleme + RPE)
 */

import type { CatalogExercise, Pattern, WorkoutDay } from './program'
import { isWorkSet } from './session.ts'
import { bantSonrakiAdim } from './bant.ts'
import type { ExerciseLog, Session } from './session'

// ── Hacim sabitleri ─────────────────────────────────────────────────────────

/** Kas başına haftalık fraksiyonel set eşikleri. */
export const VOLUME = {
  /** Bakım — kas kaybını durdurur, büyütmez. */
  MV: 6,
  /** Minimum etkili — bunun altında büyüme beklenmiyor. */
  MEV: 8,
  /** 3 gün çalışıldığında hedef. */
  TARGET: 10,
  /** 2 güne düşünce hedef. */
  TARGET_2DAY: 8,
  /** Üstü fayda getirmiyor, toparlanmayı zorluyor. */
  CAP: 14,
  /**
   * Tek seansta bir kasa yapılabilecek üst sınır. Kanıt tavanı ~11;
   * %25 güvenlik payıyla 8. Üstü aynı seansta boşa giden set demek.
   */
  PER_SESSION_CAP: 8,
} as const

export type VolumeVerdict = 'below_mv' | 'maintenance' | 'effective' | 'target' | 'over_cap'

export function volumeVerdict(sets: number): VolumeVerdict {
  if (sets < VOLUME.MV) return 'below_mv'
  if (sets < VOLUME.MEV) return 'maintenance'
  if (sets < VOLUME.TARGET) return 'effective'
  if (sets <= VOLUME.CAP) return 'target'
  return 'over_cap'
}

/**
 * Durum etiketleri.
 *
 * Önceki sürüm terimin kendisini yazıyordu: "Bakımın altında", "Bakım
 * seviyesi", "Etkili aralık". Kullanıcı haklı olarak "bakım seviyesi ne
 * demek" diye sordu — etiket bilgi vermek yerine sözlük gerektiriyordu.
 * Şimdi etiket doğrudan SONUCU söylüyor: bu hacimle kas ne yapar.
 */
export const VOLUME_LABEL: Record<VolumeVerdict, string> = {
  below_mv: 'Yetersiz',
  maintenance: 'Korur, büyütmez',
  effective: 'Büyütür',
  target: 'Hedefte',
  over_cap: 'Fazla',
}

/** Etiketin altındaki tek cümlelik açıklama — sayının neden orada olduğu. */
export const VOLUME_DETAY: Record<VolumeVerdict, string> = {
  below_mv: `Haftada ${VOLUME.MV} setin altı: kas kaybını durdurmaya bile yetmiyor.`,
  maintenance: `${VOLUME.MV}–${VOLUME.MEV} set: elindekini korur ama büyüme beklenmiyor.`,
  effective: `${VOLUME.MEV}–${VOLUME.TARGET} set: büyüme başlıyor, hedefin biraz altında.`,
  target: `${VOLUME.TARGET}–${VOLUME.CAP} set: aradığın aralık burası.`,
  over_cap: `${VOLUME.CAP} setin üstü: fayda getirmiyor, toparlanmayı zorluyor.`,
}

// ── Fraksiyonel hacim ───────────────────────────────────────────────────────

/**
 * Bir setin kaslara katkısı. Birincil kas 1.0, ikincil 0.5.
 *
 * Bu ayrım olmadan hacim yanlış çıkıyor: bileşik hareket ağırlıklı bir ev
 * programında toplam set sayısı düşük görünür ama fraksiyonel hacim yüksektir.
 * Şınav göğse 1, tricepse ve ön omuza 0.5 sayılır.
 */
export function contribution(ex: CatalogExercise): Record<string, number> {
  const out: Record<string, number> = {}
  for (const m of ex.primaryMuscles) out[m] = 1
  for (const m of ex.secondaryMuscles) out[m] = Math.max(out[m] ?? 0, 0.5)
  return out
}

/**
 * Bir antrenmanda gerçekten yapılan (işaretlenmiş) **iş** seti sayısı.
 * Isınma setleri sayılmıyor: hacme de ilerlemeye de girmemeleri gerekiyor,
 * yoksa 20 kg'lık bir ısınma "hedefin altında kaldın" sinyali üretiyor.
 */
export function doneSets(e: ExerciseLog): number {
  return e.skipped ? 0 : e.sets.filter((s) => s.done && isWorkSet(s)).length
}

/**
 * Verilen antrenmanlardaki toplam fraksiyonel hacim, kas bazında.
 * Katalogda olmayan bir hareket sessizce atlanmaz — `unknown` altında toplanır,
 * yoksa hacim sessizce eksik görünür.
 */
export function weeklyVolume(
  sessions: Session[], catalog: CatalogExercise[],
): Record<string, number> {
  const bySlug = new Map(catalog.map((c) => [c.slug, c]))
  const out: Record<string, number> = {}

  for (const s of sessions) {
    for (const e of s.exercises) {
      const n = doneSets(e)
      if (n === 0) continue

      const ex = bySlug.get(e.slug)
      if (!ex) { out.unknown = (out.unknown ?? 0) + n; continue }

      for (const [muscle, w] of Object.entries(contribution(ex)))
        out[muscle] = (out[muscle] ?? 0) + n * w
    }
  }
  return out
}

/**
 * Programın **vaat ettiği** haftalık hacim — henüz yapılmış bir şey değil.
 *
 * Bu sayı olmadan program düzenleme körlemesine oluyor: bir hareketi
 * çıkardığında ya da eklediğinde sonucun karşılığını ancak bir hafta antrenman
 * yapıp tabloya bakınca görürsün. Plan hacmi kararı anında gösteriyor.
 *
 * A/B dönüşümlü çalışıldığı için haftada `strengthPerWeek` gün, günler arasında
 * eşit paylaşılıyor: 3 gün / 2 şablon = her şablon haftada 1.5 kez.
 */
export function plannedVolume(
  days: WorkoutDay[], strengthPerWeek: number,
): Record<string, number> {
  const out: Record<string, number> = {}
  if (days.length === 0 || strengthPerWeek <= 0) return out

  const timesPerWeek = strengthPerWeek / days.length
  for (const day of days)
    for (const p of day.exercises)
      for (const [muscle, w] of Object.entries(contribution(p.exercise)))
        out[muscle] = (out[muscle] ?? 0) + p.sets * w * timesPerWeek

  return out
}

/** Bir haftaya ait tamamlanmış antrenmanlar. */
export function sessionsInWeek(sessions: Session[], weekStartIso: string, weekEndIso: string): Session[] {
  return sessions.filter(
    (s) => s.completedAt !== null && s.date >= weekStartIso && s.date <= weekEndIso)
}

// ── Gün sayısı düşünce hacmi yeniden dağıt ──────────────────────────────────

export interface Redistribution {
  weeklySets: number
  perSession: number
  /** Süre bütçesi mi sınırladı, seans tavanı mı, yoksa ikisi de değil mi. */
  limitedBy: 'none' | 'time' | 'session_cap'
  /** Bakım eşiğinin altına düştüyse bu gerçek bir kayıptır ve söylenmeli. */
  belowMaintenance: boolean
  note: string
}

/**
 * Gün sayısı düşünce haftalık hacmi azaltmak GEREKMİYOR — yeniden dağıtmak yeterli.
 * 10 fraksiyonel set ÷ 2 seans = 5 set/kas/seans, seans tavanının (8) altında.
 * Hacim sabitken frekansın hipertrofiye bağımsız katkısı pratikte sıfır.
 *
 * Gerçek kısıt fizyoloji değil, takvim. O yüzden hesap süre bütçesinden başlıyor.
 */
export function redistribute(o: {
  targetWeekly: number
  days: number
  sessionMinutes: number
  musclesPerSession: number
  useSupersets: boolean
}): Redistribution {
  if (o.days <= 0) {
    return {
      weeklySets: 0, perSession: 0, limitedBy: 'none', belowMaintenance: true,
      note: 'Hiç antrenman günü yok — kas kaybı bu noktada zaman meselesi.',
    }
  }

  // Süper set aynı sürede daha çok set sığdırıyor: %37 zaman tasarrufu,
  // hipertrofi farkı ölçülemiyor.
  const minPerSet = o.useSupersets ? 1.6 : 2.5
  const warmup = 0.85                                   // %15 ısınma/geçiş payı
  const setsFromTime = Math.floor((o.sessionMinutes * warmup) / minPerSet)
  const perSessionByTime = setsFromTime / Math.max(1, o.musclesPerSession)

  const perSession = Math.min(VOLUME.PER_SESSION_CAP, perSessionByTime)
  const weeklySets = Math.min(o.targetWeekly, perSession * o.days)

  const limitedBy: Redistribution['limitedBy'] =
    weeklySets >= o.targetWeekly ? 'none'
      : perSessionByTime < VOLUME.PER_SESSION_CAP ? 'time' : 'session_cap'

  const belowMaintenance = weeklySets < VOLUME.MV

  let note: string
  if (limitedBy === 'none') {
    note = `${o.days} günde ${round1(weeklySets)} set çıkıyor — hedefin tamamı sığıyor. `
      + `Seans başına ${round1(perSession)} set, tavanın (${VOLUME.PER_SESSION_CAP}) altında.`
  } else if (limitedBy === 'time') {
    note = `${o.sessionMinutes} dakika ${round1(weeklySets)} sete yetiyor. `
      + (o.useSupersets
        ? 'Süper set zaten açık.'
        : 'Antagonist süper sete geçersen aynı sürede ~%37 daha fazla set sığar, ölçülebilir bir kayıp olmadan.')
  } else {
    note = `Seans başına tavan (${VOLUME.PER_SESSION_CAP} set/kas) sınırlıyor. `
      + 'Aynı seansa daha fazla yığmanın faydası yok — gün eklemek gerekiyor.'
  }

  if (belowMaintenance) {
    note += ` Haftalık ${round1(weeklySets)} set bakım eşiğinin (${VOLUME.MV}) altında; `
      + 'bu dönemde kas korumak değil, kaybı yavaşlatmak hedef.'
  }

  return { weeklySets, perSession, limitedBy, belowMaintenance, note }
}

// ── Faz ─────────────────────────────────────────────────────────────────────

export type TrainingPhaseId = 'build' | 'transition' | 'newborn' | 'crisis'

export interface TrainingPhase {
  id: TrainingPhaseId
  name: string
  days: number
  sessionMinutes: number
  weeklySets: number
  targetRir: [number, number]
  /** Yük artırılıyor mu, donduruluyor mu. Bakımda yük DONAR, hacim düşer. */
  loadPolicy: 'progress' | 'freeze'
  body: string
}

/**
 * Faz, doğuma kalan gün ve eldeki zamandan seçiliyor.
 *
 * Kritik kural: bakım fazında **yükü düşürme, hacmi düşür.** Bickel'ın 32
 * haftalık çalışmasında haftada 1 seans × 3 egzersiz × 1 set bile kası ve
 * kuvveti korudu — çünkü yük korunmuştu.
 */
export function trainingPhase(daysToBirth: number | null, sessionMinutes: number): TrainingPhase {
  if (daysToBirth === null || daysToBirth > 21) {
    return {
      id: 'build', name: 'İnşa', days: 3, sessionMinutes: Math.max(40, sessionMinutes),
      weeklySets: VOLUME.TARGET, targetRir: [2, 3], loadPolicy: 'progress',
      body: 'Haftada 3 gün, A–B dönüşümlü, yük ilerliyor. Asıl kazanım burada birikiyor.',
    }
  }

  if (daysToBirth > 0) {
    return {
      id: 'transition', name: 'Geçiş', days: 2, sessionMinutes: 40,
      weeklySets: VOLUME.TARGET_2DAY, targetRir: [2, 2], loadPolicy: 'progress',
      body: 'Haftada 2 gün, antagonist süper setlerle. Haftalık hacim çok düşmüyor — '
        + 'aynı seti daha az güne dağıtmak yeterli.',
    }
  }

  if (daysToBirth > -90) {
    return {
      id: 'newborn', name: 'Yenidoğan', days: 2, sessionMinutes: 25,
      weeklySets: VOLUME.MV, targetRir: [1, 2], loadPolicy: 'freeze',
      body: 'Haftada 2 kısa seans, yük DONDURULDU. Hedef ilerlemek değil korumak; '
        + 'kaydettiğin her seans bu dönemde kazanç sayılır.',
    }
  }

  return {
    id: 'crisis', name: 'Toparlanma', days: 2, sessionMinutes: 30,
    weeklySets: VOLUME.MEV, targetRir: [2, 2], loadPolicy: 'progress',
    body: 'Uyku düzeliyor. Yükü kademeli geri al, aceleye getirme.',
  }
}

// ── Çift ilerleme ───────────────────────────────────────────────────────────

export type ProgressAction =
  | 'LOAD_UP' | 'REP_UP' | 'HOLD' | 'HOLD_CONFIRM' | 'LOAD_DOWN' | 'NONE'

export interface Suggestion {
  action: ProgressAction
  message: string
  /** Yüklenebilir harekette önerilen yeni ağırlık; değilse null. */
  nextWeightKg: number | null
  nextReps: number | null
}

/**
 * Novislerin RPE'si sistematik olarak yanılıyor: yetmezliğe kalan tekrarı
 * ortalama ~1 eksik tahmin ediyorlar ve bu 6 haftalık antrenmanda DÜZELMİYOR.
 * Yani söylediğin RPE, gerçekte olduğundan bir puan yüksek.
 */
export function correctedRpe(reported: number | null): number | null {
  return reported === null ? null : Math.max(1, reported - 1)
}

/** Alt vücut daha büyük adım kaldırıyor. */
function loadStep(pattern: Pattern | string, current: number): number {
  const lower = pattern === 'squat' || pattern === 'hinge'
  const pct = lower ? 0.05 : 0.025
  // Küçük ağırlıklarda yüzde anlamsız kalıyor; pratik en küçük adım 1 kg.
  return Math.max(1, Math.round(current * pct * 2) / 2)
}

function topWeightOf(e: ExerciseLog): number | null {
  const w = e.sets.filter((s) => s.done && isWorkSet(s) && s.weightKg != null)
    .map((s) => s.weightKg as number)
  return w.length ? Math.max(...w) : null
}

/**
 * Bir sonraki antrenman için öneri. `history` en yeniden eskiye sıralı olmalı.
 *
 * Birincil sürücü RPE değil, **tamamlanan tekrar sayısı** — o objektif.
 * RPE modifikatör ve veto sinyali. Novis RPE'sinin güvenilmezliği bunu zorunlu kılıyor.
 */
export interface BantBilgisi {
  /** Kullanıcının kurabildiği dirençler, küçükten büyüğe (bkz. bant.ts). */
  basamaklar: readonly number[]
  /** Elindeki bantlar — "15 + 30 kg" etiketini kurmak için. */
  bandsKg: readonly number[]
}

export function suggestProgression(
  history: ExerciseLog[], loadable: boolean, bant?: BantBilgisi,
): Suggestion {
  const last = history[0]
  if (!last || doneSets(last) === 0) {
    return { action: 'NONE', message: '', nextWeightKg: null, nextReps: null }
  }

  const done = last.sets.filter((s) => s.done && isWorkSet(s))
  const vals = done.map((s) => (last.unit === 'sec' ? s.seconds : s.reps) ?? 0)
  const allAtMax = vals.length >= last.targetSets && vals.every((v) => v >= last.targetHigh)
  const anyBelowMin = vals.some((v) => v < last.targetLow)
  const rpe = correctedRpe(last.rpe)
  const weight = topWeightOf(last)
  const unit = last.unit === 'sec' ? 'sn' : 'tekrar'

  // Zorlanma sinyali her şeyin önünde: yükü artırmak yerine geri çekiliyoruz.
  if (rpe !== null && rpe >= 9 && anyBelowMin) {
    const next = weight !== null ? Math.max(0, Math.round(weight * 0.925 * 2) / 2) : null
    return {
      action: 'LOAD_DOWN',
      message: next !== null
        ? `Geçen sefer hedef tekrarı tamamlayamadın ve çok zorladın. ${next} kg ile dene.`
        : 'Geçen sefer hedef tekrarı tamamlayamadın ve çok zorladın. Daha kolay bir varyanta in.',
      nextWeightKg: next, nextReps: last.targetLow,
    }
  }

  if (allAtMax) {
    // Üst sınıra ulaşmak tek başına yetmiyor: 2 ardışık seans şartı (NSCA 2-for-2).
    const prev = history[1]
    const prevAtMax = prev && doneSets(prev) > 0 &&
      prev.sets.filter((s) => s.done && isWorkSet(s))
        .map((s) => (prev.unit === 'sec' ? s.seconds : s.reps) ?? 0)
        .every((v) => v >= prev.targetHigh)

    if (rpe !== null && rpe > 8) {
      return {
        action: 'HOLD',
        message: `Üst sınırı tutturdun ama zorlanarak. Aynı yükte bir seans daha kal — `
          + 'kolaylaşınca artıracağız.',
        nextWeightKg: weight, nextReps: last.targetHigh,
      }
    }

    if (!prevAtMax) {
      return {
        action: 'HOLD_CONFIRM',
        message: `${last.targetHigh} ${unit} tamamlandı. Bir seans daha aynısını yaparsan yükü artırıyoruz `
          + '— tek seansta artırmak çok sık geri adım attırıyor.',
        nextWeightKg: weight, nextReps: last.targetHigh,
      }
    }

    // Bant: yük yüzdeyle değil BASAMAKLA artıyor. Elinde 15 ve 30 varsa
    // kurabildiğin dirençler 15/30/45; "15 → 15.4 kg" diye bir şey yok.
    // Ayrıca 15'ten 30'a geçmek yükü ikiye katlamak: önce tekrar tavanına
    // çıkılıyor, sonra bant büyüyor. Ayrıntı ve gerekçe bant.ts'te.
    if (loadable && bant && bant.basamaklar.length > 0) {
      const adim = bantSonrakiAdim({
        mevcutKg: weight,
        hedefTekrar: last.targetHigh,
        tabanTekrar: last.targetLow,
        basamaklar: bant.basamaklar,
        bandsKg: bant.bandsKg,
      })
      return adim.tur === 'load'
        ? {
            action: 'LOAD_UP',
            message: `İki seans üst üste ${last.targetHigh} ${unit} tamamlandı. ${adim.aciklama}`,
            nextWeightKg: adim.kg, nextReps: adim.tekrar,
          }
        : {
            action: 'REP_UP',
            message: `${last.targetHigh} ${unit} tamamlandı. ${adim.aciklama}`,
            nextWeightKg: adim.kg, nextReps: adim.tekrar,
          }
    }

    if (loadable && weight !== null) {
      const step = loadStep(last.pattern, weight)
      return {
        action: 'LOAD_UP',
        message: `İki seans üst üste ${last.targetHigh} ${unit} tamamlandı. `
          + `${weight} → ${weight + step} kg, tekrarı ${last.targetLow}'e döndür.`,
        nextWeightKg: weight + step, nextReps: last.targetLow,
      }
    }

    return {
      action: 'REP_UP',
      message: `Ağırlık ekleyemediğin bir hareket. Sıradaki adım: hareket açıklığını genişlet, `
        + `daha zor bir varyanta geç ya da hedefi ${last.targetHigh + 2} ${unit}'a çıkar.`,
      nextWeightKg: null, nextReps: last.targetHigh + 2,
    }
  }

  if (rpe !== null && rpe <= 6) {
    return {
      action: 'REP_UP',
      message: 'Kolay geldi. Bu sefer her sette bir iki tekrar fazla yapmayı dene.',
      nextWeightKg: weight, nextReps: Math.min(last.targetHigh, Math.max(...vals) + 2),
    }
  }

  return {
    action: 'HOLD',
    message: `Hedef aralıktasın. Aynı yükte devam, ${last.targetHigh} ${unit}'a ulaşınca artıracağız.`,
    nextWeightKg: weight, nextReps: Math.min(last.targetHigh, Math.max(...vals) + 1),
  }
}

// ── Ara verince geri dönüş ──────────────────────────────────────────────────

export interface ReturnPlan {
  loadFactor: number
  volumeFactor: number
  rampWeeks: number
  message: string
}

/**
 * Genç yetişkinlerde 28 güne kadar tam kesintide maksimal kuvvet büyük ölçüde
 * korunuyor. Panik yaptırmanın anlamı yok — ama körlemesine eski yükten devam
 * etmek de sakatlık üretiyor.
 */
export function returnAfterGap(days: number): ReturnPlan {
  if (days <= 10) {
    return { loadFactor: 1, volumeFactor: 1, rampWeeks: 0,
      message: 'Ara kısa. Kaldığın yerden devam et, bir şey değiştirmiyoruz.' }
  }
  if (days <= 21) {
    return { loadFactor: 0.95, volumeFactor: 1, rampWeeks: 1,
      message: `${days} gün ara verdin. Yükü %5 indir, bir haftada eski yüke dön.` }
  }
  if (days <= 35) {
    return { loadFactor: 0.90, volumeFactor: 0.7, rampWeeks: 2,
      message: `${days} gün ara verdin. Yük %10, hacim %30 aşağı; iki haftada geri çıkacağız.` }
  }
  return { loadFactor: 0.80, volumeFactor: 0.6, rampWeeks: 3,
    message: `${days} gün ara verdin. Yük %20 aşağı, hacim bakım seviyesine; üç haftalık kademeli dönüş. `
      + 'Kuvvetin çoğu duruyor, eksik olan bağ dokusunun toleransı.' }
}

// ── Deload ──────────────────────────────────────────────────────────────────

export interface DeloadCheck {
  score: number
  reasons: string[]
  needed: boolean
}

/**
 * Takvime bağlı planlı deload YAPMIYORUZ: 9 haftalık kontrollü çalışmada
 * hipertrofide fark çıkmadı, kuvvette kesintisiz grup daha iyiydi. Ayrıca
 * düşük hacimde çalışan ve zaten yenidoğanla takvimi bozulacak biri için
 * yapay bir hafta daha eklemek net kayıp.
 *
 * Yerine tetiklenince çalışan bir kontrol. `sessions` en yeniden eskiye sıralı.
 */
export function deloadCheck(sessions: Session[], catalog: CatalogExercise[]): DeloadCheck {
  const done = sessions.filter((s) => s.completedAt !== null).slice(0, 6)
  const reasons: string[] = []
  let score = 0

  if (done.length < 4) return { score: 0, reasons: [], needed: false }

  // 1) Aynı yükte tekrar düşüşü — son iki seansta üst üste
  const bySlug = new Map<string, ExerciseLog[]>()
  for (const s of done)
    for (const e of s.exercises)
      if (doneSets(e) > 0) bySlug.set(e.slug, [...(bySlug.get(e.slug) ?? []), e])

  let dropped = 0
  for (const logs of bySlug.values()) {
    if (logs.length < 4) continue
    const best = Math.max(...logs.slice(2).flatMap(bestReps))
    const recent = logs.slice(0, 2).map((l) => Math.max(...bestReps(l)))
    if (recent.length === 2 && recent.every((r) => r <= best - 2)) dropped++
  }
  if (dropped >= 2) {
    score += 3
    reasons.push(`${dropped} harekette aynı yükte tekrar sayısı iki seans üst üste düştü.`)
  }

  // 2) RPE sürünmesi — son iki antrenmanın ortalaması 9 ve üstü
  const avgRpe = (s: Session) => {
    const r = s.exercises.map((e) => e.rpe).filter((x): x is number => x !== null)
    return r.length ? r.reduce((a, b) => a + b, 0) / r.length : null
  }
  const recentRpe = done.slice(0, 2).map(avgRpe).filter((x): x is number => x !== null)
  if (recentRpe.length === 2 && recentRpe.every((r) => r >= 9)) {
    score += 2
    reasons.push('Son iki antrenmanda ortalama zorlanma 9 ve üstü.')
  }

  void catalog
  return { score, reasons, needed: score >= 3 }
}

function bestReps(e: ExerciseLog): number[] {
  return e.sets.filter((s) => s.done && isWorkSet(s))
    .map((s) => (e.unit === 'sec' ? s.seconds : s.reps) ?? 0)
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}

/**
 * Kas sırası artık katalogla birlikte program.ts'te duruyor — hacim tablosu ve
 * "hareket ekle" listesi aynı sırayı kullansın diye. Buradan yeniden dışa
 * veriliyor ki mevcut çağıranlar değişmesin.
 */
export { MUSCLE_ORDER } from './program.ts'

// ── Antrenman özeti ─────────────────────────────────────────────────────────

export interface SessionStats {
  /** Toplam kaldırılan yük (kg × tekrar). Ağırlıksız hareketler buna girmiyor. */
  volumeKg: number
  /** İşaretlenmiş iş seti sayısı — ısınma hariç. */
  workSets: number
  warmupSets: number
  exercises: number
  /** Atlanan hareketler — özet dürüst olsun. */
  skipped: number
  /** Kas bazında fraksiyonel set — en yüklü bölgeler. */
  byMuscle: { muscle: string; sets: number }[]
}

/**
 * Bitmiş bir antrenmanın özeti.
 *
 * Hevy'nin bitiş ekranının işe yarayan kısmı bu: "bugün ne yaptım" sorusunun
 * tek ekranlık cevabı. Süslü olması gerekmiyor, DOĞRU olması gerekiyor —
 * ısınma setleri hacme girmiyor, atlanan hareket saklanmıyor.
 */
export function sessionStats(session: Session, catalog: CatalogExercise[]): SessionStats {
  const bySlug = new Map(catalog.map((c) => [c.slug, c]))
  const muscles: Record<string, number> = {}
  let volumeKg = 0
  let workSets = 0
  let warmupSets = 0
  let skipped = 0

  for (const e of session.exercises) {
    if (e.skipped) { skipped++; continue }
    for (const s of e.sets) {
      if (!s.done) continue
      if ((s.kind ?? 'normal') === 'warmup') { warmupSets++; continue }
      workSets++
      volumeKg += (s.reps ?? 0) * (s.weightKg ?? 0)
    }
    const n = doneSets(e)
    const ex = bySlug.get(e.slug)
    if (!ex || n === 0) continue
    for (const [m, w] of Object.entries(contribution(ex))) muscles[m] = (muscles[m] ?? 0) + n * w
  }

  return {
    volumeKg: Math.round(volumeKg),
    workSets, warmupSets, skipped,
    exercises: session.exercises.filter((e) => !e.skipped && doneSets(e) > 0).length,
    byMuscle: Object.entries(muscles)
      .map(([muscle, sets]) => ({ muscle, sets }))
      .sort((a, b) => b.sets - a.sets),
  }
}

export interface PersonalRecord {
  slug: string
  name: string
  kind: 'weight' | 'reps' | 'hold' | 'first'
  message: string
}

/**
 * Kişisel rekorlar. `history` bu antrenman HARİÇ, aynı hareketin geçmiş
 * kayıtları olmalı.
 *
 * Rekor tanımı dar tutuldu: "en yüksek ağırlık" ve "aynı ağırlıkta en çok
 * tekrar". Her seansta bir rozet dağıtan bir sistem rozetleri değersizleştirir;
 * burada rekor çıkması gerçekten bir şeyin ilerlediği anlamına geliyor.
 */
export function personalRecords(
  session: Session, history: Map<string, ExerciseLog[]>,
): PersonalRecord[] {
  const out: PersonalRecord[] = []

  for (const e of session.exercises) {
    if (e.skipped || doneSets(e) === 0) continue
    const past = history.get(e.slug) ?? []
    const work = e.sets.filter((s) => s.done && isWorkSet(s))

    if (past.length === 0) {
      out.push({ slug: e.slug, name: e.name, kind: 'first',
        message: 'İlk kez kaydedildi — bundan sonrası buna göre ölçülecek.' })
      continue
    }

    const now = Math.max(0, ...work.map((s) => s.weightKg ?? 0))
    const best = Math.max(0, ...past.flatMap((p) =>
      p.sets.filter((s) => s.done && isWorkSet(s)).map((s) => s.weightKg ?? 0)))

    if (now > 0 && now > best) {
      out.push({ slug: e.slug, name: e.name, kind: 'weight',
        message: `${best} kg → ${now} kg` })
      continue
    }

    // Ağırlık aynıysa tekrar/süre rekoruna bak.
    const val = (s: { reps: number | null; seconds: number | null }) =>
      (e.unit === 'sec' ? s.seconds : s.reps) ?? 0
    const nowReps = Math.max(0, ...work.map(val))
    const pastReps = Math.max(0, ...past.flatMap((p) =>
      p.sets.filter((s) => s.done && isWorkSet(s) && (s.weightKg ?? 0) === now).map(val)))

    if (nowReps > pastReps && pastReps > 0) {
      out.push({
        slug: e.slug, name: e.name, kind: e.unit === 'sec' ? 'hold' : 'reps',
        message: e.unit === 'sec'
          ? `${pastReps} sn → ${nowReps} sn`
          : `${now > 0 ? `${now} kg'da ` : ''}${pastReps} → ${nowReps} tekrar`,
      })
    }
  }
  return out
}
